import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ArtifactAssemblyTypes, AssemblyArtifact } from 'src/app/developer/models/artifact';
import { DeveloperArtifactService } from 'src/app/developer/services/developer-artifact.service';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { Stakeholder } from 'src/app/stakeholder/models/stakeholder';
import { StakeholderService } from 'src/app/stakeholder/services/stakeholder.service';
import { MonitorGroup } from '../../models/monitor-group';
import { MonitorSchedule } from '../../models/monitor-schedule';
import { MonitorItemsGridComponent } from '../../components/monitor-items-grid/monitor-items-grid.component';
import { MonitorService } from '../../services/monitor.service';
import { MonitorAbsenceDialogComponent, MonitorAbsenceDialogResult } from "../monitor-absence-dialog/monitor-absence-dialog.component";
import { GenericModalService, ModalSettingsTemplate } from 'src/app/shared/generic-modal/generic-modal.service';
import { MonitorItem, MonitorItemClass, MonitorItemContainer } from '../../models/monitor-item';
import { AbsenceItemTemplate, Weekdays } from "../../../shared/scheduling/models/";
import { SvgIconComponent } from '../../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';

import { MonitorItemsGridComponent as MonitorItemsGridComponent_1 } from '../monitor-items-grid/monitor-items-grid.component';

@Component({
    selector: 'app-monitor-items',
    templateUrl: './monitor-items.component.html',
    styleUrls: ['./monitor-items.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, MonitorItemsGridComponent_1]
})
export class MonitorItemsComponent implements OnInit {
    monitorItems: MonitorItem[] = [];
    monitorGroups: MonitorGroup[] = [];
    monitorSchedules: MonitorSchedule[] = [];
    stakeholders: Stakeholder[] = [];
    artifacts: AssemblyArtifact[] = [];
    buttonCanPause = false;
    buttonCanResume = false;

    @ViewChild('grid') grid: MonitorItemsGridComponent;

    selectedRows: any[] = [];


    constructor(
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private monitorService: MonitorService,
        private stakeholderService: StakeholderService,
        private developerArtifactService: DeveloperArtifactService,
        private modalService: GenericModalService) {
    }

    async ngOnInit(): Promise<void> {
        await this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading monitor Items...');

            [
                this.monitorItems,
                this.monitorGroups,
                this.monitorSchedules,
                this.stakeholders,
                this.artifacts
            ] = await Promise.all([
                this.monitorService.getMonitorItems(),
                this.monitorService.getMonitorGroups(),
                this.monitorService.getMonitorSchedules(),
                this.stakeholderService.getStakeholders(),
                this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeMonitorType])
            ]);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    async onDeleteItem(item: MonitorItem): Promise<void> {
        try {
            await this.monitorService.deleteMonitorItem(item.id);
            this.toastService.showToast('Monitor item deleted...', ToastType.success);
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to delete monitor item...', ToastType.error);
        }
    }

    canPause() {
        let returnBit = true;
        if (this.selectedRows.length === 0) {
            returnBit = false;
        }
        else { // Look through all selected rows and if we find any that disqualifies the state for pause we set the bool to false. if any already is paused or is disabled we can't pause it.
            this.selectedRows.forEach(element => {
                if (element.absenceItemTemplate !== null && returnBit === true) {
                    returnBit = false;
                }
                if (element.enabled === false && returnBit === true) {
                    returnBit = false;
                }
            });
        }
        this.buttonCanPause = returnBit;
    }

    canResume() {
        let returnBit = true;
        if (this.selectedRows.length === 0) {
            returnBit = false;
        }
        else {
            this.selectedRows.forEach(element => {
                if (element.absenceItemTemplate === null && returnBit === true) {
                    returnBit = false;
                }
                if (element.enabled === false && returnBit === true) {
                    returnBit = false;
                }
            });
        }
        this.buttonCanResume = returnBit;
    }

    async pauseMonitors(): Promise<void> {
        try {
            const modalSettings = new ModalSettingsTemplate(null, 'static');
            const result: MonitorAbsenceDialogResult = await this.modalService.openComponent(MonitorAbsenceDialogComponent, null, modalSettings);
            if (result !== undefined) {
                const itemsToBeSaved: MonitorItemContainer[] = [];
                this.grid.selectedRows().forEach(element => {
                    const dateTimeNow = new Date();
                    const datetimeTo = new Date(result.toDate);
                    const fixedTemplate: AbsenceItemTemplate = {
                        Id: null,
                        Name: 'Monitor Pause: ' + element.name,
                        From: dateTimeNow,
                        To: datetimeTo,
                        Note: result.description,
                        IsGlobal: false,
                        Weekdays: Weekdays.Monday | Weekdays.Tuesday | Weekdays.Wednesday | Weekdays.Thursday | Weekdays.Friday | Weekdays.Saturday | Weekdays.Sunday
                    };
                    element.absenceItemTemplate = fixedTemplate;

                    const item: MonitorItemContainer = {
                        monitorItem: element,
                        configValues: null
                    };
                    itemsToBeSaved.push(item);
                });
                this.loadingPanelService.showLoadingPanelDirect('Pausing monitor...');
                await this.monitorService.saveMonitorItems(itemsToBeSaved);
                this.refresh();
                this.loadingPanelService.hideLoadingPanel();
            }
        }
        catch (err) {
            this.modalService.messageDirect('Exception', 'Error occured while pausing monitor').catch(() => { });
        }
    }

    async resumeMonitors(): Promise<void> {
        try {
            let itemsToBeSaved: MonitorItemContainer[] = [];
            this.grid.selectedRows().forEach(element => {
                element.absenceItemTemplate = null;

                const item: MonitorItemContainer = {
                    monitorItem: element,
                    configValues: null
                };

                itemsToBeSaved.push(item);
            });

            this.loadingPanelService.showLoadingPanelDirect('Resuming monitors...');
            await this.monitorService.saveMonitorItems(itemsToBeSaved);
            this.refresh();
            this.loadingPanelService.hideLoadingPanel();
        }
        catch (err) {
            this.modalService.messageDirect('Exception', 'Error occured while resuming monitors').catch(() => { });
        }
    }

    onSelectedRowChange(e: any): void {
        this.selectedRows = e;
        this.canPause();
        this.canResume();
    }
}
